import React from 'react';
import { useTranslation } from 'react-i18next';

import LoadThemeComponent from '../../../base/LoadThemeComponent';
import { isKenoType } from '../../../utils/LotteryHelpers';
import { displayAmount, displayJackpotAmount, formatCurrency } from '../../../utils/formatters';
import { isLottoAmerica, isLttry, isPremierDubai, isTemplateDelta, isTemplateZeta } from '../../../utils/template';
import { Route } from '../../common/Route';

import DrawDateCounter from './DrawDateCounter';

import Config from 'Config/Config';
import { getLotteryPlayButtonText } from 'utils/TextHelpers';

function shouldDisplayJackpot(lottery, isPlayPage): boolean {
    return (
        lottery.jackpot !== null &&
        ((!isPremierDubai() && !isTemplateDelta()) ||
            lottery.slug?.startsWith('mega-jackpot') ||
            (isTemplateDelta() && isPlayPage))
    );
}

function Lottery({ lottery, loading, isPlayPage }) {
    const { t } = useTranslation();

    const hasLotteryDrawInterval =
        isKenoType(lottery.type) && lottery.next_draw_date != null && lottery.next_next_draw_date != null;

    const lotteryJackpot = lottery.jackpot || 0;

    const buttonText = getLotteryPlayButtonText(lottery, isPlayPage);

    const defaultView = () => (
        <div
            className={`lottery-card flex-grow-1 position-relative ${lottery.is_promoted ? 'promoted-card' : ''} ${
                loading ? 'loading-overlay' : ''
            }`}
        >
            <div className="lottery-card-content d-flex flex-column justify-content-center align-items-center text-center">
                <img
                    src={lottery.ballImage}
                    alt={`${lottery.name} ${t('lotteryList.ball')}`}
                    className="lottery-card-img mx-auto h-auto"
                />
                <div className="lottery-card-texts d-flex flex-column align-items-center">
                    <p className="lottery-card-name">{lottery.name}</p>
                    <p className="lottery-card-jackpot">{formatCurrency(lotteryJackpot)}</p>
                    <p className="lottery-card-counter">
                        <i className="far fa-clock" />
                        <DrawDateCounter
                            nextDrawDate={lottery.next_draw_date}
                            drawLoop={hasLotteryDrawInterval ? lottery.drawsInterval : 0}
                            nextNextDrawDate={lottery.next_next_draw_date}
                        />
                    </p>
                </div>
            </div>
            <div className="lottery-card-content-bottom">
                <button className="lottery-card-btn">{t('ggWorldJackpot.playNow')}</button>
            </div>
        </div>
    );

    const baseView = () => (
        <div className={`lottery-wrapper row no-gutters text-center text-xl-left ${loading ? 'loading-overlay' : ''}`}>
            <div
                className="lottery-wrapper-background flex-column"
                style={
                    {
                        '--background-img': `url(${lottery.ballImage})`,
                    } as React.CSSProperties
                }
            >
                <div className="row no-gutters">
                    <div className="col-auto d-none d-xl-block img-wrapper">
                        <img
                            loading="lazy"
                            className="base_ball"
                            src={lottery.ballImage}
                            alt={`${lottery.name} ${t('lotteryList.ball')}`}
                        />
                    </div>
                    <div className="col lottery-data">
                        <h2>{lottery.name || '-'}</h2>
                        {shouldDisplayJackpot(lottery, isPlayPage) && (
                            <h3 data-dusk={`jackpot-${lottery.display_slug}`}>
                                {isTemplateZeta()
                                    ? displayJackpotAmount(lotteryJackpot, lottery.currency_code)
                                    : displayAmount(lotteryJackpot, Config.get('app.currency'))}
                            </h3>
                        )}

                        <img
                            loading="lazy"
                            className="base_ball d-block d-xl-none"
                            src={lottery.ballImage}
                            alt={`${lottery.name} ${t('lotteryList.ball')}`}
                            data-dusk={`ball-${lottery.display_slug}`}
                        />
                        {!isLottoAmerica() && (
                            <span
                                className="countdown next-draw d-block"
                                data-date={lottery.next_draw_date != null ? lottery.next_draw_date : 0}
                                data-enable-zero-prefix="0"
                                data-show-units="1"
                                data-date-loop={
                                    hasLotteryDrawInterval ? lottery.drawsInterval : lottery.next_next_draw_date || 0
                                }
                                data-dusk={`counter-${lottery.display_slug}`}
                            >
                                {isLttry() ? t('lotteryList.drawIn') : t('lotteryList.nextDrawIn')}
                                <strong>
                                    <DrawDateCounter
                                        nextDrawDate={lottery.next_draw_date}
                                        drawLoop={hasLotteryDrawInterval ? lottery.drawsInterval : 0}
                                        nextNextDrawDate={lottery.next_next_draw_date}
                                    />
                                </strong>
                            </span>
                        )}
                    </div>
                </div>
                <div className="row no-gutters play-button">
                    {isLottoAmerica() && (
                        <span
                            className="countdown next-draw d-block"
                            data-date={lottery.next_draw_date != null ? lottery.next_draw_date : 0}
                            data-enable-zero-prefix="0"
                            data-show-units="1"
                            data-date-loop={
                                hasLotteryDrawInterval ? lottery.drawsInterval : lottery.next_next_draw_date || 0
                            }
                            data-dusk={`counter-${lottery.display_slug}`}
                        >
                            <i className="far fa-clock"></i>
                            <strong>
                                <DrawDateCounter
                                    nextDrawDate={lottery.next_draw_date}
                                    drawLoop={hasLotteryDrawInterval ? lottery.drawsInterval : 0}
                                    nextNextDrawDate={lottery.next_next_draw_date}
                                />
                            </strong>
                        </span>
                    )}

                    <div className={isLottoAmerica() ? 'col-12-md' : 'col'}>
                        <a
                            href={Route('play', { lottery: lottery.display_slug })}
                            className="btn btn-primary btn-submit d-block"
                            data-dusk={`widget-play-${lottery.display_slug}`}
                        >
                            {buttonText}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );

    return <LoadThemeComponent defaultView={defaultView} baseView={baseView} />;
}

export default Lottery;
